import { useQuery, useQueryClient } from '@tanstack/react-query'
import { type Address, getAddress } from 'viem'
import { usePublicClient, useReadContracts } from 'wagmi'
import type { NormalizedEVaultInfo } from './types'
import { useEulerIndexerEVaults, setEVaultInCache } from './evaultsQuery'
import { fetchEVaultOnchain, fetchEVaultsOnchain } from './onchainEVaultFetcher'
import { E_EARN_VAULT_CHAIN_ID } from '@/config/consts'
import { getPerspectiveAddresses } from '@/config/euler-contracts'
import { eulerEarnGovernedPerspectiveAbi } from 'abis/evm/eulerEarnGovernedPerspective'

/**
 * Hook to get EVault info with indexer-first, onchain-fallback pattern
 *
 * Priority:
 * 1. Check Euler Indexer cache (React Query cache)
 * 2. If not found, fetch from onchain and cache the result
 *
 * This ensures we always have EVault data while preferring the faster indexer
 */
export function useEVaultInfo(address: Address | undefined, chainId: number = E_EARN_VAULT_CHAIN_ID) {
  const queryClient = useQueryClient()
  const publicClient = usePublicClient({ chainId })

  // First, try to get from Euler Indexer cache
  const { data: vaultMap, isLoading: isIndexerLoading, isError: isIndexerError } = useEulerIndexerEVaults(chainId)

  // Check if EVault exists in indexer cache
  const indexerVault = address ? vaultMap?.get(getAddress(address) as Address) : undefined

  // Only fetch from onchain if:
  // 1. We have an address and publicClient
  // 2. Indexer is not loading AND (indexer has error OR vault not found in cache)
  const shouldFetchOnchain = Boolean(address && publicClient && !isIndexerLoading && (isIndexerError || !indexerVault))

  const {
    data: onchainVault,
    isLoading: isOnchainLoading,
    isError: isOnchainError,
  } = useQuery({
    queryKey: ['evault-onchain', chainId, address],
    queryFn: async () => {
      if (!address || !publicClient) throw new Error('No address or publicClient provided')

      const vault = await fetchEVaultOnchain(publicClient, chainId, address)

      // Cache the result for future lookups
      setEVaultInCache(queryClient, chainId, vault)

      return vault
    },
    enabled: shouldFetchOnchain,
    staleTime: 10 * 60 * 1000, // 10 minutes for onchain data
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
  })

  return {
    vault: indexerVault ?? onchainVault,
    isLoading: isIndexerLoading || (shouldFetchOnchain && isOnchainLoading),
    isError: isIndexerError && isOnchainError,
    source: indexerVault ? ('indexer' as const) : onchainVault ? ('onchain' as const) : undefined,
  }
}

/**
 * Hook to get multiple EVaults with indexer-first, onchain-fallback pattern
 */
export function useEVaultsInfo(addresses: Address[], chainId: number = E_EARN_VAULT_CHAIN_ID) {
  const queryClient = useQueryClient()
  const publicClient = usePublicClient({ chainId })

  // First, try to get from Euler Indexer cache
  const { data: vaultMap, isLoading: isIndexerLoading, isError: isIndexerError } = useEulerIndexerEVaults(chainId)

  // Find which vaults are missing from cache
  const missingAddresses = addresses.filter((address) => {
    if (!vaultMap) return true
    try {
      return !vaultMap.has(getAddress(address) as Address)
    } catch {
      return true
    }
  })

  // Fetch missing vaults from onchain
  const shouldFetchOnchain = !isIndexerLoading && missingAddresses.length > 0 && !!publicClient

  const { data: onchainVaults, isLoading: isOnchainLoading } = useQuery({
    queryKey: ['evaults-onchain', chainId, missingAddresses],
    queryFn: async () => {
      if (!publicClient) throw new Error('No publicClient provided')

      const vaults = await fetchEVaultsOnchain(publicClient, chainId, missingAddresses)

      // Cache all fetched vaults
      for (const vault of vaults.values()) {
        setEVaultInCache(queryClient, chainId, vault)
      }

      return vaults
    },
    enabled: shouldFetchOnchain,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 2,
  })

  // Combine indexer and onchain results
  const vaults = new Map<Address, NormalizedEVaultInfo>()

  for (const address of addresses) {
    try {
      const normalizedAddress = getAddress(address) as Address

      // Check indexer first
      const indexerVault = vaultMap?.get(normalizedAddress)
      if (indexerVault) {
        vaults.set(normalizedAddress, indexerVault)
        continue
      }

      // Check onchain fallback
      const onchainVault = onchainVaults?.get(normalizedAddress)
      if (onchainVault) {
        vaults.set(normalizedAddress, onchainVault)
      }
    } catch {
      // Invalid address, skip
    }
  }

  return {
    vaults,
    isLoading: isIndexerLoading || (shouldFetchOnchain && isOnchainLoading),
    isIndexerError,
    missingCount: addresses.length - vaults.size,
  }
}

/**
 * Helper to get EVault from a vaults map by address
 * Handles address normalization and error handling
 */
export function getEVaultFromMap(
  vaults: Map<Address, NormalizedEVaultInfo>,
  address: Address | undefined,
): NormalizedEVaultInfo | undefined {
  if (!address) return undefined
  try {
    const normalizedAddress = getAddress(address) as Address
    return vaults.get(normalizedAddress)
  } catch {
    return undefined
  }
}

/**
 * Get all EVault addresses from the indexer cache
 */
export function useEVaultAddresses(chainId: number = E_EARN_VAULT_CHAIN_ID): Address[] {
  const { data: vaultMap } = useEulerIndexerEVaults(chainId)
  return vaultMap ? Array.from(vaultMap.keys()) : []
}

/**
 * Get EVaults array from indexer (useful for displaying lists)
 * WITH ONCHAIN FALLBACK: If indexer fails, fetches vault addresses from Euler Earn perspective
 * and gets vault details from onchain
 */
export function useEVaultsList(chainId: number = E_EARN_VAULT_CHAIN_ID) {
  const queryClient = useQueryClient()
  const publicClient = usePublicClient({ chainId })
  const perspectiveAddresses = getPerspectiveAddresses(chainId)

  // Step 1: Try to get from Euler Indexer
  const { data: vaultMap, isLoading: isIndexerLoading, isError: isIndexerError } = useEulerIndexerEVaults(chainId)

  // Step 2: If indexer fails, get vault addresses from Euler Earn perspective contracts onchain
  const shouldFetchPerspectiveAddresses = isIndexerError && !isIndexerLoading && !!perspectiveAddresses

  const perspectiveContracts = perspectiveAddresses
    ? [
        {
          address: perspectiveAddresses.eulerEarnGoverned,
          abi: eulerEarnGovernedPerspectiveAbi,
          functionName: 'verifiedArray',
          chainId,
        },
        {
          address: perspectiveAddresses.eulerEarnFactory,
          abi: eulerEarnGovernedPerspectiveAbi,
          functionName: 'verifiedArray',
          chainId,
        },
      ]
    : []

  const { data: perspectiveResults } = useReadContracts({
    contracts: perspectiveContracts as any,
    query: {
      enabled: shouldFetchPerspectiveAddresses,
      staleTime: 60_000, // 1 minute
    },
  })

  // Combine vault addresses from all perspectives (excluding duplicates)
  const vaultAddressesFromPerspectives: Address[] = []
  if (perspectiveResults) {
    const seenAddresses = new Set<string>()
    for (const result of perspectiveResults) {
      if (result.status === 'success' && Array.isArray(result.result)) {
        for (const addr of result.result as Address[]) {
          const normalized = addr.toLowerCase()
          if (!seenAddresses.has(normalized)) {
            seenAddresses.add(normalized)
            vaultAddressesFromPerspectives.push(addr)
          }
        }
      }
    }
  }

  // Step 3: If we have vault addresses from perspectives, fetch vault details onchain
  const shouldFetchOnchain =
    isIndexerError && !isIndexerLoading && vaultAddressesFromPerspectives.length > 0 && !!publicClient

  const { data: onchainVaults, isLoading: isOnchainLoading } = useQuery({
    queryKey: ['evaults-list-onchain-fallback', chainId],
    queryFn: async () => {
      if (!publicClient) throw new Error('No publicClient provided')

      const vaults = await fetchEVaultsOnchain(publicClient, chainId, vaultAddressesFromPerspectives)

      // Cache all fetched vaults
      for (const vault of vaults.values()) {
        setEVaultInCache(queryClient, chainId, vault)
      }

      return vaults
    },
    enabled: shouldFetchOnchain,
    staleTime: 10 * 60 * 1000, // 10 minutes for onchain data
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
  })

  // Return indexer data if available, otherwise onchain data
  const finalVaultMap = vaultMap || onchainVaults
  const vaults = finalVaultMap ? Array.from(finalVaultMap.values()) : []

  return {
    vaults,
    isLoading: isIndexerLoading || (shouldFetchOnchain && isOnchainLoading),
    isError: isIndexerError && !onchainVaults,
  }
}
